import type { Strings } from '@/constants/strings'
import { toApiError, type ApiErrorKind } from '@/services/apiError'

export interface UserError {
  /** Banner/toast basligi. */
  title: string
  /** Kullaniciya gosterilecek metin. Asla ham JS/XML/HTML icermez. */
  message: string
  kind: ApiErrorKind
  code: string | null
  status: number | null
}

/**
 * Hata turune gore yerel metin. `null` donmesi "cagiranin baglamsal metni daha
 * iyi" demektir (ornegin bir 400'de "Turbin eklenirken..." metni, jenerik
 * "bilgilerde sorun var" metninden daha aciklayicidir).
 */
const messageForKind = (kind: ApiErrorKind, t: Strings): string | null => {
  switch (kind) {
    case 'network':
      return t.errors.network
    case 'timeout':
      return t.errors.timeout
    case 'auth':
      return t.errors.sessionExpired
    case 'permission':
      return t.errors.permission
    case 'ratelimit':
      return t.errors.rateLimit
    case 'server':
      return t.errors.server
    case 'notfound':
      return t.errors.notFoundResource
    case 'conflict':
      return t.errors.conflict
    case 'validation':
    case 'unknown':
      return null
  }
}

/**
 * Herhangi bir hatayi kullaniciya gosterilebilir hale getirir.
 *
 * Oncelik sirasi:
 *   1. Backend'in mesaji  -- backend TR ve aciklayici yazar, ASLA ezilmez
 *   2. Hata turune gore yerel metin (ag/yetki/oturum/rate-limit...)
 *   3. Cagiranin baglamsal yedek metni ("Turbin eklenirken hata olustu")
 *   4. Jenerik metin
 *
 * @param fallback Cagiranin baglamsal metni. i18n'den gelmeli.
 */
export const toUserError = (error: unknown, t: Strings, fallback?: string): UserError => {
  const apiError = toApiError(error)

  const message =
    apiError.serverMessage ??
    messageForKind(apiError.kind, t) ??
    fallback ??
    t.errors.generic

  return {
    title: t.errors.title,
    message,
    kind: apiError.kind,
    code: apiError.code,
    status: apiError.status,
  }
}

/** `setError(...)` gibi duz string bekleyen yerler icin kisayol. */
export const toUserMessage = (error: unknown, t: Strings, fallback?: string): string =>
  toUserError(error, t, fallback).message

/**
 * Bir servis yanitindaki hatayi metne cevirir.
 * Servis katmani throw etmez; `{success, message}` doner. `message` bos gelirse
 * baglamsal yedek metin kullanilir.
 */
export const responseErrorMessage = (
  response: { message?: string | undefined },
  t: Strings,
  fallback?: string
): string => {
  const message = response.message?.trim()
  if (message) return message
  return fallback ?? t.errors.generic
}

/**
 * Hatayi konsola yazar. Kullaniciya gosterim AYRI bir istir --
 * bu yardimci onun yerine gecmez, yanina gelir.
 */
export const logError = (context: string, error: unknown): void => {
  const apiError = toApiError(error)
  console.error(`[${context}]`, {
    kind: apiError.kind,
    status: apiError.status,
    code: apiError.code,
    message: apiError.message,
    details: apiError.details,
  })
}
